import React, { useState } from "react";
import { useHistory, Redirect } from "react-router-dom";
import {
  Avatar,
  CssBaseline,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  makeStyles,
  CircularProgress,
  Fade,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Lock, Email, VpnKey } from "@material-ui/icons";
import { useFirebase } from "../../Firebase/FirebaseContext";
import FinalForm from "../Elements/FinalForm";
import InputText from "../Inputs/InputText";
import CustomButton from "../Buttons/CustomButton";
import {
  browserLocalPersistence,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import {
  poserSectionsAutorisees,
  CHAMP_FIRESTORE,
} from "../../Utils/sectionsReservees";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
  },
  image: {
    backgroundImage: "url(/images/login.jpg)",
    backgroundRepeat: "no-repeat",
    backgroundColor: theme.palette.grey[900],
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}ee 0%, ${theme.palette.primary.dark}dd 50%, #1a237e 100%)`,
      animation: "$gradientShift 15s ease infinite",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: "-50%",
      left: "-50%",
      width: "200%",
      height: "200%",
      background:
        "radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)",
      backgroundSize: "50px 50px",
      animation: "$float 20s linear infinite",
    },
  },
  brandContent: {
    position: "relative",
    zIndex: 2,
    color: "#FFFFFF",
    padding: theme.spacing(6),
    textAlign: "center",
    animation: "$fadeInLeft 0.8s ease-out",
  },
  brandLogo: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    backgroundColor: "rgba(255,255,255,0.2)",
    backdropFilter: "blur(10px)",
    margin: "0 auto",
    marginBottom: theme.spacing(3),
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    border: "2px solid rgba(255,255,255,0.3)",
  },
  brandTitle: {
    fontWeight: 800,
    fontSize: "3rem",
    marginBottom: theme.spacing(2),
    textShadow: "0 4px 20px rgba(0,0,0,0.3)",
    letterSpacing: "2px",
  },
  brandSubtitle: {
    fontSize: "1.2rem",
    opacity: 0.95,
    fontWeight: 300,
    letterSpacing: "1px",
  },
  paper: {
    margin: theme.spacing("auto", 4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    maxWidth: 500,
    padding: theme.spacing(6),
    background: "#FFFFFF",
  },
  loginCard: {
    width: "100%",
    animation: "$fadeInRight 0.8s ease-out",
  },
  logoContainer: {
    marginBottom: theme.spacing(4),
    textAlign: "center",
  },
  avatar: {
    margin: "0 auto",
    width: theme.spacing(10),
    height: theme.spacing(10),
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 8px 24px ${theme.palette.primary.main}44`,
    animation: "$pulse 2s ease-in-out infinite",
  },
  title: {
    fontWeight: 800,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(3),
    fontSize: "2.2rem",
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    fontSize: "1rem",
  },
  form: {
    width: "100%",
  },
  inputWrapper: {
    marginBottom: theme.spacing(3),
    position: "relative",
  },
  submit: {
    margin: theme.spacing(4, 0, 2),
    height: 56,
    fontSize: "1.1rem",
    fontWeight: 600,
    borderRadius: 12,
    boxShadow: `0 4px 14px ${theme.palette.primary.main}44`,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
    },
    "&:disabled": {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[500],
    },
  },
  loadingButton: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  footer: {
    marginTop: theme.spacing(4),
  },
  divider: {
    margin: theme.spacing(3, 0),
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: theme.palette.divider,
    },
  },
  "@keyframes fadeInLeft": {
    "0%": {
      opacity: 0,
      transform: "translateX(-50px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },
  "@keyframes fadeInRight": {
    "0%": {
      opacity: 0,
      transform: "translateX(50px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.05)",
    },
  },
  "@keyframes gradientShift": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.8,
    },
  },
  "@keyframes float": {
    "0%": {
      transform: "translate(0, 0)",
    },
    "100%": {
      transform: "translate(50px, 50px)",
    },
  },
}));

export default function Login() {
  const { firebase } = useFirebase();
  const history = useHistory();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);

  const onSubmit = async (values) => {
    setLoading(true);
    setError("");
    const email = values.email.toLowerCase().trim();
    setPersistence(firebase.auth, browserLocalPersistence).then(() => {
      signInWithEmailAndPassword(firebase.auth, email, values.password)
        .then((userCredential) => {
          firebase.currentUser = userCredential.user;
          const docRef = doc(firebase.db, "users", userCredential.user.email);
          getDoc(docRef)
            .then((docData) => {
              if (docData.exists() && docData.data().belongs_to) {
                getDoc(docData.data().belongs_to)
                  .then((labodata) => {
                    const laboratoryId =
                      docData.data().belongs_to._key.path.segments[6];

                    // Stockage des informations de laboratoire
                    localStorage.setItem("laboratory", laboratoryId); // Laboratoire d'origine de l'utilisateur
                    localStorage.setItem("currentLab", laboratoryId); // Laboratoire actuel (initialement le même)
                    localStorage.setItem(
                      "structure",
                      labodata.data().structure
                    );
                    // Le jeton reste dans le SDK Firebase : il n'est plus
                    // copie dans localStorage (audit, constat E6).

                    // Les sections du menu que cette personne a le droit de
                    // voir (retour du 08/09). Une fiche sans ce champ — donc
                    // toutes les fiches existantes — n'ouvre rien : voir
                    // Utils/sectionsReservees.
                    poserSectionsAutorisees(docData.data()[CHAMP_FIRESTORE]);

                    console.log(
                      `Utilisateur connecté au laboratoire: ${laboratoryId}, structure: ${
                        labodata.data().structure
                      }`
                    );

                    // Redirection vers le laboratoire d'origine de l'utilisateur à la connexion
                    if (
                      window.location.pathname.includes("/laboratories/") &&
                      !window.location.pathname.includes(
                        `/laboratories/${laboratoryId}`
                      )
                    ) {
                      // Si l'utilisateur est déjà sur une page de laboratoire mais pas la bonne
                      window.location.href = `/laboratories/${laboratoryId}`;
                    } else {
                      // Redirection vers la page d'accueil
                      window.location.href = "/home";
                    }
                  })
                  .catch((error) => {
                    console.error(
                      "Erreur lors de la récupération des données du laboratoire:",
                      error
                    );
                    setLoading(false);
                    setError("Erreur lors de la récupération du laboratoire");
                    setShowError(true);
                  });
              } else {
                console.error("L'utilisateur n'a pas de laboratoire associé");
                setLoading(false);
                setError("Aucun laboratoire associé à ce compte");
                setShowError(true);
              }
            })
            .catch((error) => {
              console.error(
                "Erreur lors de la récupération des données utilisateur:",
                error
              );
              setLoading(false);
              setError("Erreur lors de la récupération des données");
              setShowError(true);
            });
        })
        .catch((e) => {
          console.log("Login unsuccessful", e);
          setLoading(false);
          setError("Email ou mot de passe incorrect");
          setShowError(true);
        });
    });
  };

  return (
    <Grid container component="main" className={classes.root}>
      {localStorage.laboratory && <Redirect push to="/home" />}
      <CssBaseline />

      <Grid item xs={false} sm={4} md={7} className={classes.image}>
        <div className={classes.brandContent}>
          <Avatar className={classes.brandLogo}>
            <Lock style={{ fontSize: 48 }} />
          </Avatar>
          <Typography variant="h1" className={classes.brandTitle}>
            LYBO
          </Typography>
          <Typography variant="h5" className={classes.brandSubtitle}>
            Plateforme de gestion des laboratoires
          </Typography>
        </div>
      </Grid>

      <Grid
        item
        xs={12}
        sm={8}
        md={5}
        component={Paper}
        elevation={0}
        square
        style={{ display: "flex" }}
      >
        <div className={classes.paper}>
          <Fade in timeout={800}>
            <div className={classes.loginCard}>
              <div className={classes.logoContainer}>
                <Avatar className={classes.avatar}>
                  <VpnKey style={{ fontSize: 42 }} />
                </Avatar>
                <Typography
                  component="h1"
                  variant="h3"
                  className={classes.title}
                >
                  Connexion
                </Typography>
                <Typography variant="body1" className={classes.subtitle}>
                  Accédez à votre espace sécurisé
                </Typography>
              </div>

              <FinalForm onSubmit={onSubmit} style={classes.form}>
                <Box className={classes.inputWrapper}>
                  <InputText
                    name="email"
                    label="Adresse email"
                    required
                    disabled={loading}
                  />
                </Box>
                <Box className={classes.inputWrapper}>
                  <InputText
                    name="password"
                    label="Mot de passe"
                    type="password"
                    required
                    disabled={loading}
                  />
                </Box>

                <Box className={classes.loadingButton}>
                  <CustomButton
                    type="submit"
                    className={classes.submit}
                    title={loading ? "Connexion en cours..." : "Se connecter"}
                    fullWidth={true}
                    disabled={loading}
                  />
                  {loading && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                      style={{ color: "#FFFFFF" }}
                    />
                  )}
                </Box>
              </FinalForm>

              <Box className={classes.footer}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                >
                  {"© "}
                  <Link
                    color="primary"
                    href="https://gyra-lean.com/"
                    style={{ textDecoration: "none", fontWeight: 600 }}
                  >
                    GYRA
                  </Link>
                  {" " + new Date().getFullYear()} - Tous droits réservés
                </Typography>
              </Box>
            </div>
          </Fade>
        </div>
      </Grid>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          elevation={6}
          variant="filled"
        >
          {error}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

